import {
  Design,
  Areas,
  Floorplan,
  Group,
  InterAreaConnection,
  LogicalSpace,
  JustInTimeRoutingConfig,
  Obstacle,
  VehicleType,
  LoadType,
  Flow,
  Track,
  TrafficRulesCatalog,
  MetaData,
  Templates,
  FlowStop,
  FlowLayer,
  ReferenceImage,
  UxOnly,
  FileReference,
  HighwayArea,
} from '@thive/fddm-types';

import { computeItemsBoundingBoxExtent } from '@/modules/common/helpers/boundingBox';
import { IdUtils } from '@/modules/common/utils';
import { unique, uniqueBy } from '@/modules/common/helpers/array';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { Area, Template } from '@/modules/floorplanDesign/types';
import { FloorplanRevision } from '@/modules/localDevHelpers/FloorplanDesign';
import { FloorplanServiceConnector } from '@/modules/api/FloorplanServiceConnector/FloorplanServiceConnector';
import {
  createEmptyAreas,
  createEmptyFlowLayer,
  createEmptyTemplates,
  filterAreasByIds,
  getSupportedVehicleIds,
  pushTemplateToItsCollection,
  setSupportedVehicles,
  templatesToFlatCollection,
  getAreasRelatedToJitConfigs,
  excludeAreasByIds,
} from '@/modules/floorplanDesign/helpers/helpers';
import {
  areasToFlatCollection,
  hasNoTemplates,
  hasNoAreas,
  pushAreaToItsCollection,
  isHighwayArea,
  findDefaultSupportVehiclesForArea,
} from '@/modules/floorplanDesign/helpers';
import { InterAreaConnectionUtils } from '@/modules/connections/interAreaConnection';
import {
  getGroupBorderId,
  getPoseIdsFromPois,
} from '@/modules/floorplanDesign/mappers/helpers/helpers';
import { FddmPreferenceValues } from '@/modules/floorplanDesign/preferences';
import { isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';
import { areaFactory } from '@/modules/floorplanDesign/factories';
import { FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { areaSupportsCutOutIds } from '@/modules/floorplanDesign/helpers/typeguards';
import {
  createLoadTypeDimensionsById,
  fitAreaStationSpecToAreaWidth,
  fitAreaStationSpecsToAreaWidth,
} from '@/modules/floorplanDesign/helpers/stationCapacity';
import { isUuid } from '@/helpers/utils';
import { FpsServiceFactory } from '@/modules/floorplanV2/service/FpsServiceFactory';
import { Step } from '@/modules/floorplanV2/types';
import { VehicleServiceFactory } from '@/modules/vehicles/service/VehicleServiceFactory';
import { IVehicleService } from '@/modules/vehicles/service/IVehicleService';
import { VehiclesUtil } from '@/modules/vehicles/vehiclesUtil';
import { logger } from '@common/logging/logger';
import { ICollisionService } from '@/modules/collision/service/ICollisionService';
import { CollisionServiceFactory } from '@/modules/collision/service/CollisionServiceFactory';
import { useCollisionStore } from '@/modules/collision/store/useCollisionStore';
import { Collision } from '@/modules/collision/types';
import {
  buildAreaWithCutOutIds,
  getAreaItsDeeplyReferencedCutOutAreaIds,
  getObstaclesReferencedByCutOutIds,
  getLogicalSpacesReferencedByCutOutIds,
  getPersistedOverlapAction,
  getPersistedOverlaps,
  getCutoutsFromCollisions,
} from '@/modules/collision/helpers/cutouts';
import {
  getAreasFromCollisions,
  getAreasFromPersistedCollisions,
} from '@/modules/collision/helpers/collision';

import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { ICanvasService } from '@/modules/canvas/services/Canvas/ICanvasService';
import { IFddmService } from './IFddmService';
import {
  TransformSelectionMode,
  transformAreaByMode,
  transformLogicalSpaceByMode,
  transformObstacleByMode,
} from './selectionTransform';
import { usePointCloudUIStore } from '@/modules/pointCloud/store';
import { getFlowCanvasElementIds } from '../../mappers/flowMapper';
import { adjustAreaForDominantAxisChange } from '../../helpers/areaDimensionAxisChange';

export class FddmServiceImpl implements IFddmService {
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private pointCloudUIStore: typeof usePointCloudUIStore;
  private collisionStore: typeof useCollisionStore;
  private canvasSvc: ICanvasService;
  private vehicleSvc: IVehicleService;
  private collisionSvc: ICollisionService;

  public constructor(
    floorplanDesignStore: typeof useFloorplanDesignStore,
    pointCloudUIStore: typeof usePointCloudUIStore,
    collisionStore: typeof useCollisionStore,
  ) {
    this.floorplanDesignStore = floorplanDesignStore;
    this.pointCloudUIStore = pointCloudUIStore;
    this.collisionStore = collisionStore;
    this.canvasSvc = CanvasServiceFactory.getInstance().createCanvasService();
    this.vehicleSvc = VehicleServiceFactory.getInstance().createVehicleService();
    this.collisionSvc = CollisionServiceFactory.getInstance().createCollisionService();
  }

  initialize(revision: FloorplanRevision): void {
    this.floorplanDesignStore.getState().initialize(revision);

    const { newCollisions, removedCollisions } = this.collisionSvc.initialize({
      areas: revision.design.areas,
    });
    const areasWithCutOuts = this.syncCutoutIds(
      newCollisions,
      removedCollisions,
      revision.design.areas,
    );
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);
  }

  async regenerateAll(): Promise<void> {
    const { design } = this.floorplanDesignStore.getState();
    if (hasNoAreas(design.areas)) return;
    await this.generateFullFloorplanAndApply();
  }

  getModel(): FloorplanRevision {
    return this.floorplanDesignStore.getState().getModel();
  }

  //
  // MetaData
  //

  getMetaData(): MetaData {
    return this.floorplanDesignStore.getState().getMetaData();
  }

  setMetaData(metaData: MetaData): void {
    this.floorplanDesignStore.getState().setMetaData(metaData);
  }

  //
  // Reference image
  //

  getReferenceImage(): UxOnly['referenceImage'] {
    return this.floorplanDesignStore.getState().uxonly.referenceImage;
  }

  addReferenceImage(referenceImageData: ReferenceImage): void {
    const {
      uxonly: { referenceImage },
      setReferenceImage,
    } = this.floorplanDesignStore.getState();

    if (referenceImage) {
      throw Error(
        'FddmServiceImpl.addReferenceImage: Can not add reference image. Reference image already exists.',
      );
    }

    setReferenceImage(referenceImageData);
  }

  updateReferenceImage(referenceImageData: ReferenceImage): void {
    const {
      uxonly: { referenceImage },
      setReferenceImage,
    } = this.floorplanDesignStore.getState();

    if (!referenceImage) {
      throw Error(
        'FddmServiceImpl.updateReferenceImage: Can not update reference image. No reference image exists yet.',
      );
    }

    setReferenceImage(referenceImageData);
  }

  deleteReferenceImage(): void {
    const {
      uxonly: { referenceImage },
      setReferenceImage,
    } = this.floorplanDesignStore.getState();

    if (!referenceImage) {
      throw Error(
        'FddmServiceImpl.deleteReferenceImage: Can not delete reference image. No reference image exists.',
      );
    }

    setReferenceImage(undefined);
  }

  //
  // point cloud
  //

  addPointCloudInputFileRef(fileRef: string): void {
    this.floorplanDesignStore.getState().setPointCloudInputFileRef(fileRef);
    const { inputFileActions } = this.pointCloudUIStore.getState();
    inputFileActions.setUploadProcess({ status: 'success' });
  }

  addPointCloudOutputFileRef(fileRef: string): void {
    this.floorplanDesignStore.getState().setPointCloudOutputFileRef(fileRef);
    const { outputFileActions } = this.pointCloudUIStore.getState();
    outputFileActions.setUploadProcess({ status: 'success' });
  }

  deletePointCloudInputFileRef(): void {
    const { inputFileActions: uiStateActions } = this.pointCloudUIStore.getState();
    uiStateActions.setUploadProcess({ status: 'idle' });
    this.floorplanDesignStore.getState().setPointCloudInputFileRef(undefined);
  }

  deletePointCloudOutputFileRef(): void {
    const { outputFileActions: uiStateActions } = this.pointCloudUIStore.getState();
    uiStateActions.setUploadProcess({ status: 'idle' });

    this.floorplanDesignStore.getState().setPointCloudOutputFileRef(undefined);
  }

  //
  // Design
  //

  getDesign(): Design {
    return this.floorplanDesignStore.getState().getModel().design;
  }

  setDesign(design: Design): void {
    this.invalidateValidation();
    this.floorplanDesignStore.getState().setDesign(design);

    this.canvasSvc.updateElements(
      {
        areas: design.areas,
        logicalSpaces: design.logicalSpaces,
        obstacles: design.obstacles,
        flowLayer: design.flows,
        templates: design.templates,
        interAreaConnections: design.interAreaConnections,
        jitRoutingConfigs: design.justInTimeRoutingConfigs,
      },
      { preserveInteractivity: true },
    );

    this.generatePartialFloorplanAndApply([
      ...areasToFlatCollection(design.areas).map((e) => e.id),
      ...design.obstacles.map((e) => e.id),
      ...design.logicalSpaces.map((e) => e.id),
    ]);
  }

  //
  // Areas
  //

  getArea(id: string): Area | undefined {
    return this.floorplanDesignStore.getState().getAreaById(id);
  }

  async addAreas(areas: Areas): Promise<void> {
    const fittedAreas = this.fitStationSpecsToAreaWidth(areas);

    const { newCollisions, removedCollisions } = this.collisionSvc.update({
      updatedAreas: fittedAreas,
    });
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions, fittedAreas);

    this.invalidateValidation();

    // only update already existing areas and only add new areas to the store
    const existingAreas: Areas = excludeAreasByIds(
      areasWithCutOuts,
      areasToFlatCollection(areas).map((a) => a.id),
    );
    const newAreas: Areas = filterAreasByIds(
      areasWithCutOuts,
      areasToFlatCollection(areas).map((a) => a.id),
    );
    this.floorplanDesignStore.getState().addAreas(newAreas);
    this.floorplanDesignStore.getState().updateAreas(existingAreas);

    await this.canvasSvc.updateElements({ areas: newAreas });

    // Only the areas that were actually changed
    this.updateInterAreaConnectionsBasedOnAreas(newAreas);

    const areasAsArray: Area[] = areasToFlatCollection(areasWithCutOuts);
    this.generatePartialFloorplanAndApply(areasAsArray.map((e) => e.id));
  }

  updateAreas(areas: Areas): void {
    const axisAdjustedAreas = this.adjustAreasForAxisChanges(areas);
    const fittedAreas = this.fitStationSpecsToAreaWidth(axisAdjustedAreas);

    const { newCollisions, removedCollisions } = this.collisionSvc.update({
      updatedAreas: fittedAreas,
    });
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions, fittedAreas);

    this.invalidateValidation();
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);
    this.canvasSvc.updateElements({ areas: areasWithCutOuts });

    // Only the areas that were actually changed
    this.updateInterAreaConnectionsBasedOnAreas(
      filterAreasByIds(
        areasWithCutOuts,
        areasToFlatCollection(fittedAreas).map((a) => a.id),
      ),
    );

    const areasAsArray: Area[] = areasToFlatCollection(areasWithCutOuts);
    const jitRelatedAreaIds = this.getJitRelatedAreaIds(areasAsArray.map((a) => a.id));

    this.generatePartialFloorplanAndApply([...areasAsArray.map((e) => e.id), ...jitRelatedAreaIds]);
  }

  updateArea(area: Area): void {
    const axisAdjustedArea = this.adjustAreaForDominantAxisChange(area);
    const fittedArea = this.fitStationSpecToAreaWidth(axisAdjustedArea);

    const areas: Areas = createEmptyAreas();
    pushAreaToItsCollection(areas, fittedArea);

    const { newCollisions, removedCollisions } = this.collisionSvc.update({
      updatedAreas: areas,
    });
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions, areas);

    this.invalidateValidation();
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);
    this.canvasSvc.updateElements({ areas: areasWithCutOuts });
    // Only the areas that were actually changed: neighbours that are in areasWithCutOuts
    // just because their cutOutIds changed must not get their connections re-detected.
    this.updateInterAreaConnectionsBasedOnAreas(
      filterAreasByIds(areasWithCutOuts, [fittedArea.id]),
    );

    const jitRelatedAreaIds = this.getJitRelatedAreaIds([fittedArea.id]);

    this.generatePartialFloorplanAndApply([fittedArea.id, ...jitRelatedAreaIds]);
  }

  deleteAreas(areas: Areas): void {
    const {
      interAreaConnectionActions,
      getRelatedFlows,
      deleteFlows,
      deleteAreas: deleteStoreAreas,
      deleteFlowStops,
      deleteGroups,
      getRelatedFlowStops,
      getGroupsByAreaIds,
      deleteJitRoutingConfigs,
      getHighwayBySegmentId,
      design,
    } = this.floorplanDesignStore.getState();

    const deletedAreas = areasToFlatCollection(areas);
    const deletedAreaIds = deletedAreas.map((area) => area.id);
    const deletedAreaIdSet = new Set(deletedAreaIds);
    const deletedSegmentIds = deletedAreas
      .filter(isHighwayArea)
      .flatMap((area) => area.segments.map((segment) => segment.id));

    const deletedHighwayIds = new Set(deletedAreas.filter(isHighwayArea).map((area) => area.id));
    const jitConfigIdsToDelete = design.justInTimeRoutingConfigs
      .filter((config) =>
        deletedHighwayIds.has(getHighwayBySegmentId(config.highwayAreaId)?.id ?? ''),
      )
      .map((config) => config.id);

    const relatedFlowStops = getRelatedFlowStops(deletedAreaIds);
    const flowStopIdsToDelete: string[] = [];

    const relatedGroups = getGroupsByAreaIds(deletedAreaIds);
    const groupIdsToDelete: string[] = [];
    relatedGroups.forEach((group) => {
      const newAreaIds = group.areaIds.filter((areaId) => !deletedAreaIdSet.has(areaId));
      if (!isAtLeast1(newAreaIds)) groupIdsToDelete.push(group.id);
    });

    const flowsToUpdate = createEmptyFlowLayer();

    relatedFlowStops.forEach((flowStop) => {
      const newAreaIds = flowStop.areaIds.filter((areaId) => !deletedAreaIdSet.has(areaId));
      if (!isAtLeast1(newAreaIds)) {
        flowStopIdsToDelete.push(flowStop.id);
        return;
      }

      flowsToUpdate.flowStops.push({
        ...flowStop,
        areaIds: newAreaIds,
      });
    });

    const flowIdsToRemove = getRelatedFlows([...deletedAreaIds, ...flowStopIdsToDelete]).map(
      (flow) => flow.id,
    );
    const interAreaConnectionsToRemove = interAreaConnectionActions.getRelatedTo([
      ...deletedAreaIds,
      ...deletedSegmentIds,
    ]);
    const interAreaConnectionIdsToRemove = interAreaConnectionsToRemove.map((con) => con.id);
    const floorplanElementIdsToRemove = this.removeFloorplanElementsByAreas(areas);

    deleteFlows(flowIdsToRemove);
    deleteStoreAreas(deletedAreaIds);
    deleteFlowStops(flowStopIdsToDelete);
    deleteGroups(groupIdsToDelete);
    if (jitConfigIdsToDelete.length > 0) {
      deleteJitRoutingConfigs(jitConfigIdsToDelete);
    }

    const { newCollisions, removedCollisions } = this.collisionSvc.update({ deletedAreas: areas });
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions);
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);

    interAreaConnectionActions.deleteByIds(interAreaConnectionIdsToRemove);
    flowsToUpdate.flowStops.forEach((flowStop) =>
      this.floorplanDesignStore.getState().updateFlowStop(flowStop),
    );

    this.canvasSvc.setActiveTool('cursor');
    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids: unique([
        ...deletedAreaIds,
        ...flowStopIdsToDelete,
        ...flowIdsToRemove.flatMap(getFlowCanvasElementIds),
        ...interAreaConnectionIdsToRemove,
        ...floorplanElementIdsToRemove,
        ...groupIdsToDelete,
        ...groupIdsToDelete.map((id) => getGroupBorderId(id)),
        ...jitConfigIdsToDelete,
      ]),
    });

    this.canvasSvc.updateElements({ flowLayer: flowsToUpdate });
    this.invalidateValidation();
    this.generatePartialFloorplanAndApply(areasToFlatCollection(areasWithCutOuts).map((a) => a.id));
  }

  //
  // Obstacles
  //

  getObstacle(id: string): Obstacle | undefined {
    return this.floorplanDesignStore.getState().getObstacleById(id);
  }

  async addObstacles(obstacles: Obstacle[]): Promise<void> {
    const { newCollisions, removedCollisions } = this.collisionSvc.update({
      updatedObstacles: obstacles,
    });
    this.floorplanDesignStore.getState().addObstacles(obstacles);
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions);
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);

    this.invalidateValidation();

    await this.canvasSvc.updateElements({ obstacles });

    this.generatePartialFloorplanAndApply([
      ...obstacles.map((o) => o.id),
      ...areasToFlatCollection(areasWithCutOuts).map((a) => a.id),
    ]);
  }

  updateObstacle(obstacle: Obstacle): void {
    const { newCollisions, removedCollisions } = this.collisionSvc.update({
      updatedObstacles: [obstacle],
    });
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions);
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);

    this.invalidateValidation();
    this.floorplanDesignStore.getState().updateObstacle(obstacle);
    this.canvasSvc.updateElements({ obstacles: [obstacle] });

    this.generatePartialFloorplanAndApply([
      obstacle.id,
      ...areasToFlatCollection(areasWithCutOuts).map((a) => a.id),
    ]);
  }

  updateObstacles(obstacles: Obstacle[]): void {
    const { newCollisions, removedCollisions } = this.collisionSvc.update({
      updatedObstacles: obstacles,
    });
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions);
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);

    this.invalidateValidation();
    obstacles.forEach((obstacle) => this.floorplanDesignStore.getState().updateObstacle(obstacle));
    this.canvasSvc.updateElements({ obstacles });

    this.generatePartialFloorplanAndApply([
      ...obstacles.map((o) => o.id),
      ...areasToFlatCollection(areasWithCutOuts).map((a) => a.id),
    ]);
  }

  deleteObstacles(obstacles: Obstacle[]): void {
    const ids = obstacles.map((o) => o.id);
    if (ids.length === 0) {
      throw Error('FddmServiceImpl.deleteObstacles: No obstacle ids supplied.');
    }

    const { newCollisions, removedCollisions } = this.collisionSvc.update({
      deletedObstacles: obstacles,
    });
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions);
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);

    this.invalidateValidation();
    this.floorplanDesignStore.getState().deleteObstacles(ids);
    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids,
    });

    this.generatePartialFloorplanAndApply([
      ...obstacles.map((o) => o.id),
      ...areasToFlatCollection(areasWithCutOuts).map((a) => a.id),
    ]);
  }

  //
  // Inter area connections
  //

  getInterAreaConnection(id: string): InterAreaConnection | undefined {
    return this.floorplanDesignStore.getState().interAreaConnectionActions.getById(id);
  }

  addInterAreaConnections(connections: InterAreaConnection[]): boolean {
    let result = false;

    if (connections.length === 0) {
      throw Error('FddmServiceImpl.addInterAreaConnections: No connections supplied.');
    }

    const created = this.floorplanDesignStore
      .getState()
      .interAreaConnectionActions.create(connections);

    if (created.length > 0) {
      this.canvasSvc.updateElements({ interAreaConnections: created });
      this.invalidateValidation();
      result = true;
    }

    return result;
  }

  updateInterAreaConnections(connections: InterAreaConnection[]): void {
    // Any connection modified by the user is marked explicitly created so the
    // auto-detection logic never removes or switches it without user intent.
    const markedExplicit = connections.map((c) => ({ ...c, createdExplicitly: true }));
    markedExplicit.forEach((connection) =>
      this.floorplanDesignStore.getState().interAreaConnectionActions.update(connection),
    );
    this.canvasSvc.updateElements({ interAreaConnections: markedExplicit });
    this.invalidateValidation();
  }

  deleteInterAreaConnections(connections: InterAreaConnection[]): void {
    const ids = connections.map((c) => c.id);
    if (ids.length === 0) {
      throw Error('FddmServiceImpl.deleteInterAreaConnections: No connection ids supplied.');
    }

    this.floorplanDesignStore.getState().interAreaConnectionActions.deleteByIds(ids);
    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids,
    });
    this.invalidateValidation();
  }

  //
  // Logical Spaces
  //

  getLogicalSpace(id: string): LogicalSpace | undefined {
    return this.floorplanDesignStore.getState().getLogicalSpaceById(id);
  }

  async addLogicalSpaces(logicalSpaces: LogicalSpace[]): Promise<void> {
    const { newCollisions, removedCollisions } = this.collisionSvc.update({
      updatedLogicalSpaces: logicalSpaces,
    });
    this.floorplanDesignStore.getState().addLogicalSpaces(logicalSpaces);
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions);
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);

    this.invalidateValidation();

    await this.canvasSvc.updateElements({ logicalSpaces });

    this.generatePartialFloorplanAndApply([
      ...logicalSpaces.map((ls) => ls.id),
      ...areasToFlatCollection(areasWithCutOuts).map((a) => a.id),
    ]);
  }

  updateLogicalSpace(logicalSpace: LogicalSpace): void {
    const { newCollisions, removedCollisions } = this.collisionSvc.update({
      updatedLogicalSpaces: [logicalSpace],
    });
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions);
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);

    this.invalidateValidation();
    this.floorplanDesignStore.getState().updateLogicalSpace(logicalSpace);
    this.canvasSvc.updateElements({ logicalSpaces: [logicalSpace] });

    this.generatePartialFloorplanAndApply([
      logicalSpace.id,
      ...areasToFlatCollection(areasWithCutOuts).map((a) => a.id),
    ]);
  }

  deleteLogicalSpaces(logicalSpaces: LogicalSpace[]): void {
    const ids = logicalSpaces.map((ls) => ls.id);
    if (ids.length === 0) {
      throw Error('FddmServiceImpl.deleteLogicalSpaces: No logical space ids supplied.');
    }

    const { newCollisions, removedCollisions } = this.collisionSvc.update({
      deletedLogicalSpaces: logicalSpaces,
    });
    const areasWithCutOuts = this.syncCutoutIds(newCollisions, removedCollisions);
    this.floorplanDesignStore.getState().updateAreas(areasWithCutOuts);

    this.invalidateValidation();
    this.floorplanDesignStore.getState().deleteLogicalSpaces(ids);
    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids,
    });

    this.generatePartialFloorplanAndApply([
      ...ids,
      ...areasToFlatCollection(areasWithCutOuts).map((a) => a.id),
    ]);
  }

  //
  // Templates
  //

  getTemplate(id: string): Template | undefined {
    return this.floorplanDesignStore.getState().getTemplateById(id);
  }

  // TODO: is this one needed?
  addTemplates(templates: Templates): void {
    this.floorplanDesignStore.getState().addTemplates(templates);
    this.updateInterAreaConnectionsBasedOnTemplates(templates);
    this.canvasSvc.updateElements({ templates });
  }

  async addTemplate(
    template: Template,
    areas: Areas,
    interAreaConnections: InterAreaConnection[],
    flowLayer: FlowLayer,
  ): Promise<void> {
    this.floorplanDesignStore.getState().addAreas(areas);

    const templateToAdd = createEmptyTemplates();
    pushTemplateToItsCollection(templateToAdd, template);
    this.floorplanDesignStore.getState().addTemplates(templateToAdd);
    this.updateInterAreaConnectionsBasedOnTemplates(templateToAdd);

    const created = this.floorplanDesignStore
      .getState()
      .interAreaConnectionActions.create(interAreaConnections);

    this.floorplanDesignStore.getState().addFlowStops(flowLayer.flowStops);
    this.floorplanDesignStore.getState().addFlows(flowLayer.flows);

    await this.canvasSvc.updateElements({
      templates: templateToAdd,
      areas,
      interAreaConnections: created,
      flowLayer,
    });

    const areasAsArray: Area[] = areasToFlatCollection(areas);
    await this.generatePartialFloorplanAndApply(areasAsArray.map((e) => e.id));
  }

  updateTemplate(template: Template): void {
    this.floorplanDesignStore.getState().updateTemplate(template);

    const templates = createEmptyTemplates();
    pushTemplateToItsCollection(templates, template);
    this.updateInterAreaConnectionsBasedOnTemplates(templates);
  }

  updateTemplates(templates: Templates): void {
    templatesToFlatCollection(templates).forEach((template) => this.updateTemplate(template));
    this.updateInterAreaConnectionsBasedOnTemplates(templates);
  }

  async regenerateTemplate(
    template: Template,
    areas: Areas,
    interAreaConnections: InterAreaConnection[],
    flowLayer: FlowLayer,
    previousAreas: Areas,
  ): Promise<void> {
    const {
      getRelatedFlows,
      getTemplateByIdSilently,
      getTemplateByAreaId,
      addAreas,
      addTemplates,
      deleteTemplates,
      updateTemplate,
      addFlowStops,
      addFlows,
      getAreaByAreaIdOrSegmentId,
      getRelatedFlowStops,
      interAreaConnectionActions: { create, getRelatedTo },
    } = this.floorplanDesignStore.getState();

    const previousAreasFlat = areasToFlatCollection(previousAreas);
    const previousAreaIds = previousAreasFlat.map((area) => area.id);

    // If template is not in FDDM, add a new one
    const currentTemplate =
      getTemplateByIdSilently(template.group.id) ??
      previousAreaIds.map((areaId) => getTemplateByAreaId(areaId)).find(Boolean);

    if (!currentTemplate) {
      await this.addTemplate(template, areas, interAreaConnections, flowLayer);
      return;
    }

    const previousAreaOrSegmentIds = previousAreasFlat.flatMap((area) => {
      if (!isHighwayArea(area)) return [area.id];

      return [area.id, ...area.segments.map((segment) => segment.id)];
    });
    const explicitConnectionsToRestore = getRelatedTo(previousAreaOrSegmentIds).filter(
      (connection) => connection.createdExplicitly,
    );

    // When regenerating a template, we want to keep all flows that are related to the template
    // But not flows that are related to flow stops that will be removed
    const flowStopIdsThatWillBeRemoved = getRelatedFlowStops(previousAreaIds).map(
      (flowStop) => flowStop.id,
    );

    const flowsToCarryOver = getRelatedFlows(previousAreaIds)
      .filter(
        (flow) =>
          !flowLayer.flows.some(
            (f) =>
              f.sourceAreaOrFlowStopId === flow.sourceAreaOrFlowStopId &&
              f.destinationAreaOrFlowStopId === flow.destinationAreaOrFlowStopId,
          ),
      )
      .filter(
        (flow) =>
          !flowStopIdsThatWillBeRemoved.includes(flow.sourceAreaOrFlowStopId) ||
          !flowStopIdsThatWillBeRemoved.includes(flow.destinationAreaOrFlowStopId),
      );

    flowLayer.flows.push(...flowsToCarryOver);

    // Areas
    this.deleteAreas(previousAreas);
    addAreas(areas);

    if (currentTemplate.group.id === template.group.id) {
      updateTemplate(template);
    } else {
      const templatesToAdd = createEmptyTemplates();
      pushTemplateToItsCollection(templatesToAdd, template);

      deleteTemplates([currentTemplate.group.id]);
      addTemplates(templatesToAdd);

      this.canvasSvc.removeCanvasElements({
        method: 'byId',
        ids: [currentTemplate.group.id, getGroupBorderId(currentTemplate.group.id)],
      });
    }

    // Connections
    this.updateInterAreaConnectionsBasedOnAreas(areas);
    const createdTemplateConnections = create(interAreaConnections);
    const templateConnectionIds = new Set(interAreaConnections.map((connection) => connection.id));
    const restorableConnections = explicitConnectionsToRestore.filter(
      (connection) =>
        !templateConnectionIds.has(connection.id) &&
        Boolean(getAreaByAreaIdOrSegmentId(connection.idA)) &&
        Boolean(getAreaByAreaIdOrSegmentId(connection.idB)) &&
        (!previousAreaOrSegmentIds.includes(connection.idA) ||
          !previousAreaOrSegmentIds.includes(connection.idB)),
    );
    const restoredConnections = create(restorableConnections);

    // Flow layer
    addFlowStops(flowLayer.flowStops);
    addFlows(flowLayer.flows);

    // Update canvas
    const templates = createEmptyTemplates();
    pushTemplateToItsCollection(templates, template);
    await this.canvasSvc.updateElements({
      templates,
      areas,
      interAreaConnections: [...createdTemplateConnections, ...restoredConnections],
      flowLayer,
    });

    // PFG
    const areasAsArray: Area[] = areasToFlatCollection(areas);
    this.generatePartialFloorplanAndApply(areasAsArray.map((area) => area.id));
  }

  deleteTemplates(templates: Templates): void {
    const canvasElementsToRemove: string[] = [];
    const templateIds = templatesToFlatCollection(templates).map((t) => t.group.id);
    canvasElementsToRemove.push(...templateIds);

    templatesToFlatCollection(templates).forEach((template) => {
      canvasElementsToRemove.push(...template.group.areaIds);

      const areas: Areas = createEmptyAreas();
      template.group.areaIds
        .map((id) => this.getArea(id))
        .forEach((area) => {
          if (area) {
            pushAreaToItsCollection(areas, area);
          }
        });

      this.deleteAreas(areas);

      const { getRelatedTo } = this.floorplanDesignStore.getState().interAreaConnectionActions;
      const relatedConnections = getRelatedTo([
        ...areasToFlatCollection(areas).flatMap((area) =>
          isHighwayArea(area) ? area.segments.map((segment) => segment.id) : area.id,
        ),
      ]);

      canvasElementsToRemove.push(...relatedConnections.map((conn) => conn.id));
    });

    this.floorplanDesignStore.getState().deleteTemplates(templateIds);
    this.invalidateValidation();

    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids: canvasElementsToRemove,
    });
  }

  //
  // Groups
  //

  getGroup(id: string): Group | undefined {
    return this.floorplanDesignStore.getState().getGroupById(id);
  }

  addGroups(groups: Group[]): void {
    this.floorplanDesignStore.getState().addGroups(groups);
    this.canvasSvc.updateElements({ groups });
  }

  updateGroup(group: Group): void {
    this.floorplanDesignStore.getState().updateGroup(group);
    this.canvasSvc.updateElements({ groups: [group] });
  }

  deleteGroups(groups: Group[]): void {
    const { deleteGroups } = this.floorplanDesignStore.getState();

    const ids = groups.map((group) => group.id);

    if (ids.length === 0) {
      throw Error('FddmServiceImpl.deleteGroups: No group ids supplied.');
    }

    const areas: Areas = createEmptyAreas();
    const logicalSpaces: LogicalSpace[] = [];
    const obstacles: Obstacle[] = [];

    groups.forEach((group) => {
      group.areaIds.forEach((id) => {
        const area = this.getArea(id);
        const logicalSpace = this.getLogicalSpace(id);
        const obstacle = this.getObstacle(id);

        if (area) {
          pushAreaToItsCollection(areas, area);
          return;
        }

        if (logicalSpace) {
          logicalSpaces.push(logicalSpace);
          return;
        }

        if (obstacle) {
          obstacles.push(obstacle);
        }
      });
    });

    if (!hasNoAreas(areas)) {
      this.deleteAreas(areas);
    }

    if (logicalSpaces.length > 0) {
      this.deleteLogicalSpaces(logicalSpaces);
    }

    if (obstacles.length > 0) {
      this.deleteObstacles(obstacles);
    }

    deleteGroups(ids);

    groups.forEach((group) => {
      this.canvasSvc.ungroup(group.id);
    });

    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids: ids.map((id) => getGroupBorderId(id)),
    });
  }

  ungroupGroups(groups: Group[]): void {
    const { deleteGroups } = this.floorplanDesignStore.getState();
    const ids = groups.map((g) => g.id);
    if (ids.length === 0) {
      throw Error('FddmServiceImpl.ungroupGroups: No group ids supplied.');
    }

    deleteGroups(ids);
    groups.forEach((group) => {
      this.canvasSvc.ungroup(group.id);
    });

    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids: ids.map((id) => getGroupBorderId(id)),
    });
  }

  //
  // Jit Routing Points
  //

  getJitRoutingConfig(id: string): JustInTimeRoutingConfig | undefined {
    return this.floorplanDesignStore.getState().getJitRoutingConfigById(id);
  }

  addJitRoutingConfigs(jitRoutingConfigs: JustInTimeRoutingConfig[]): void {
    this.floorplanDesignStore.getState().addJitRoutingConfigs(jitRoutingConfigs);
    this.canvasSvc.updateElements({ jitRoutingConfigs });
    this.invalidateValidation();
    this.generatePartialFloorplanAndApply(
      getAreasRelatedToJitConfigs(
        jitRoutingConfigs,
        this.floorplanDesignStore.getState().getHighwayBySegmentId,
      ),
    );
  }

  updateJitRoutingConfig(jitRoutingConfig: JustInTimeRoutingConfig): void {
    this.floorplanDesignStore.getState().updateJitRoutingConfig(jitRoutingConfig);
    this.canvasSvc.updateElements({ jitRoutingConfigs: [jitRoutingConfig] });
    this.invalidateValidation();
    this.generatePartialFloorplanAndApply(
      getAreasRelatedToJitConfigs(
        [jitRoutingConfig],
        this.floorplanDesignStore.getState().getHighwayBySegmentId,
      ),
    );
  }

  deleteJitRoutingConfigs(jitRoutingConfigs: JustInTimeRoutingConfig[]): void {
    const ids = jitRoutingConfigs.map((jrc) => jrc.id);
    if (ids.length === 0) {
      throw Error('FddmServiceImpl.deleteJitRoutingConfigs: No JIT routing config ids supplied.');
    }

    this.floorplanDesignStore.getState().deleteJitRoutingConfigs(ids);
    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids,
    });
  }

  //
  // Flows
  //

  getFlow(id: string): Flow | undefined {
    return this.floorplanDesignStore.getState().getFlowById(id);
  }

  getFlowsByAreaId(areaId: string): Flow[] {
    return this.floorplanDesignStore.getState().getFlowsByAreaId(areaId);
  }

  addFlows(newFlows: Flow[]): void {
    this.floorplanDesignStore.getState().addFlows(newFlows);
    const flowLayer = createEmptyFlowLayer();
    flowLayer.flows = newFlows;
    this.canvasSvc.updateElements({ flowLayer });
  }

  updateFlow(flow: Flow): void {
    // propagate hourly order count changes to whole journey
    if (this.getFlow(flow.id)?.hourlyOrderCounts[0] !== flow.hourlyOrderCounts[0]) {
      const newFlows: Flow[] = [flow, ...this.propagatedFlows(flow)];
      this.updateFlows(newFlows);
      return;
    }

    this.floorplanDesignStore.getState().updateFlow(flow);
    const flowLayer = createEmptyFlowLayer();
    flowLayer.flows = [flow];
    this.canvasSvc.updateElements({ flowLayer });
  }

  updateFlows(updatedFlows: Flow[]): void {
    updatedFlows.forEach((flow) => this.floorplanDesignStore.getState().updateFlow(flow));
    const flowLayer = createEmptyFlowLayer();
    flowLayer.flows = updatedFlows;
    this.canvasSvc.updateElements({ flowLayer });
  }

  deleteFlows(flows: Flow[]): void {
    const ids = flows.map((flow) => flow.id);
    if (ids.length === 0) {
      throw Error('FddmServiceImpl.deleteFlows: No flow ids supplied.');
    }

    this.floorplanDesignStore.getState().deleteFlows(ids);
    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids: ids.flatMap(getFlowCanvasElementIds),
    });
  }

  //
  // FlowStops
  //

  getFlowStop(id: string): FlowStop | undefined {
    return this.floorplanDesignStore.getState().getFlowStopById(id);
  }

  getFlowStopByAreaId(areaId: string): FlowStop | undefined {
    return this.floorplanDesignStore.getState().getFlowStopByAreaId(areaId);
  }

  addFlowStops(flowStops: FlowStop[]): void {
    this.floorplanDesignStore.getState().addFlowStops(flowStops);
    const flowLayer = createEmptyFlowLayer();
    flowLayer.flowStops = flowStops;
    this.canvasSvc.updateElements({ flowLayer });
  }

  updateFlowStop(flowStop: FlowStop): void {
    this.floorplanDesignStore.getState().updateFlowStop(flowStop);
    const flowLayer = createEmptyFlowLayer();
    flowLayer.flowStops = [flowStop];
    this.canvasSvc.updateElements({ flowLayer });
  }

  deleteFlowStops(flowStops: FlowStop[]): void {
    const { deleteFlowStops, getRelatedFlows, deleteFlows } = this.floorplanDesignStore.getState();

    const flowStopIds = flowStops.map((fs) => fs.id);
    if (flowStopIds.length === 0) {
      throw Error('FddmServiceImpl.deleteFlowStops: No flow stop ids supplied.');
    }

    deleteFlowStops(flowStopIds);
    const flowIdsToRemove = getRelatedFlows(flowStopIds).map((flow) => flow.id);
    deleteFlows(flowIdsToRemove);

    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids: [...flowStopIds, ...flowIdsToRemove],
    });
  }

  //
  // Vehicles
  //

  getVehicleType(id: string): VehicleType | undefined {
    return this.floorplanDesignStore.getState().getVehicleTypeById(id);
  }

  addVehicleTypes(vehicleTypes: VehicleType[]): void {
    this.floorplanDesignStore.getState().addVehicleTypes(vehicleTypes);
  }

  addVehiclesToHighways(vehicleTypes: FloorplanRevision['setup']['vehicleTypes']): void {
    if (vehicleTypes.length === 0) {
      return;
    }

    const {
      design: { areas },
    } = this.floorplanDesignStore.getState();

    const updatedHighways: HighwayArea[] = [];

    areas.highwayAreas.forEach((area) => {
      const currentSupportedVehicleIds = getSupportedVehicleIds(area);
      const newSupportedVehicleIds = [...currentSupportedVehicleIds];

      vehicleTypes.forEach((vehicleType) => {
        if (!newSupportedVehicleIds.includes(vehicleType.id)) {
          newSupportedVehicleIds.push(vehicleType.id);
        }
      });

      if (currentSupportedVehicleIds.length === newSupportedVehicleIds.length) {
        return;
      }

      updatedHighways.push(setSupportedVehicles(area, newSupportedVehicleIds));
    });

    if (updatedHighways.length === 0) {
      return;
    }

    this.updateAreas({
      highwayAreas: updatedHighways,
      rackingAreas: [],
      singularStationAreas: [],
      stationaryAreas: [],
      manualAreas: [],
    });
  }

  updateVehicleType(vehicleType: VehicleType): void {
    this.floorplanDesignStore.getState().updateVehicleType(vehicleType);
    this.invalidateValidation();
  }

  deleteVehicleTypes(vehicleTypes: VehicleType[]): void {
    const vehicleTypeIds = vehicleTypes.map((vt) => vt.id);
    const idsSet = new Set(vehicleTypeIds);
    const floorplanElementIdsToRemove = this.getFloorplanElementIdsByVehicleTypeIds(vehicleTypeIds);
    const { setup, design } = this.floorplanDesignStore.getState();

    design.logicalSpaces.forEach((logicalSpace) => {
      const hasAffectedRules = logicalSpace.trafficRules.some(
        (rule) =>
          rule.type === 'VEHICLE_MODE' &&
          (!rule.vehicleModeExtension?.vehicleTypeId ||
            idsSet.has(rule.vehicleModeExtension.vehicleTypeId)),
      );

      if (!hasAffectedRules) {
        return;
      }

      const nextTrafficRules = logicalSpace.trafficRules.flatMap((rule) => {
        if (rule.type !== 'VEHICLE_MODE') {
          return [rule];
        }

        const ruleVehicleTypeId = rule.vehicleModeExtension?.vehicleTypeId;

        // Vehicle-mode rules without a concrete vehicle are invalid and should be removed.
        if (!ruleVehicleTypeId) {
          return [];
        }

        if (!idsSet.has(ruleVehicleTypeId)) {
          return [rule];
        }

        return [];
      });

      this.updateLogicalSpace({
        ...logicalSpace,
        trafficRules: nextTrafficRules,
      });
    });

    if (floorplanElementIdsToRemove.length > 0) {
      this.canvasSvc.removeCanvasElements({
        method: 'byId',
        ids: floorplanElementIdsToRemove,
      });
    }

    this.floorplanDesignStore.getState().deleteVehicleTypes(vehicleTypeIds);
    this.floorplanDesignStore.getState().deleteVehicleLayouts(vehicleTypeIds);
    this.vehicleSvc.removeVehicleDetails(vehicleTypeIds);

    // Remove deleted vehicle types from areas
    const newVehicleTypes = setup.vehicleTypes.filter((item) => !idsSet.has(item.id));
    const fallbackVehicleTypeId = newVehicleTypes[0]?.id;
    const updatedAreaIds: string[] = []; // Keep track of updated area ids, for PFG
    const areas = areasToFlatCollection(design.areas);

    const updatedAreas: Areas = {
      highwayAreas: [],
      rackingAreas: [],
      singularStationAreas: [],
      stationaryAreas: [],
      manualAreas: [],
    };

    areas.forEach((area) => {
      const currentSupportedVehicleIds = getSupportedVehicleIds(area);
      const hasSelectedShuttleVehicles = currentSupportedVehicleIds.some((id) =>
        VehiclesUtil.isShuttleVehicle(id),
      );
      if (hasSelectedShuttleVehicles) {
        return;
      }

      const supportedVehicleIdsToRemove = currentSupportedVehicleIds.filter((item) =>
        idsSet.has(item),
      );

      if (supportedVehicleIdsToRemove.length > 0) {
        updatedAreaIds.push(area.id);

        const newSupportedVehicleIds = currentSupportedVehicleIds.filter((id) => !idsSet.has(id));

        if (newSupportedVehicleIds.length === 0) {
          const defaultSupportedVehicles = findDefaultSupportVehiclesForArea(
            newVehicleTypes,
            'forklifts',
            isHighwayArea(area) ? 'includeAll' : 'limitToOne',
          );

          if (defaultSupportedVehicles.length > 0) {
            newSupportedVehicleIds.push(...defaultSupportedVehicles.map((item) => item.id));
          } else if (fallbackVehicleTypeId) {
            newSupportedVehicleIds.push(fallbackVehicleTypeId);
          }
        }

        const newArea = setSupportedVehicles(area, newSupportedVehicleIds);
        pushAreaToItsCollection(updatedAreas, newArea);
      }
    });

    this.updateAreas(updatedAreas);

    // Generate floorplan
    const model = this.floorplanDesignStore.getState().getModel();

    FloorplanServiceConnector.getInstance()
      .generateFloorplan(model)
      .then((response) => {
        if (!response?.floorplan) {
          return;
        }

        this.replaceFullFloorplan(response.floorplan);
      })
      .catch((error: unknown) => {
        logger.error('Error regenerating floorplan after deleting vehicle types:', error);
      });
  }

  //
  // Load Carriers
  //

  getLoadType(id: string): LoadType | undefined {
    return this.floorplanDesignStore.getState().getLoadTypeById(id);
  }

  addLoadTypes(loadTypes: LoadType[]): void {
    this.floorplanDesignStore.getState().addLoadTypes(loadTypes);
  }

  deleteLoadTypes(loadTypes: LoadType[]): void {
    const loadTypeIds = loadTypes.map((lt) => lt.id);
    this.floorplanDesignStore.getState().deleteLoadTypes(loadTypeIds);
  }

  //
  // Catalog
  //

  setCatalogTrafficRules(trafficRules: TrafficRulesCatalog): void {
    this.floorplanDesignStore.getState().trafficRules.set(trafficRules);
  }

  getAllFileReferences(): FileReference[] {
    return this.floorplanDesignStore.getState().getFileReferences();
  }

  getFileReferences(type: FileReference['type']): FileReference[] {
    return this.floorplanDesignStore
      .getState()
      .getFileReferences()
      .filter((ref) => ref.type === type);
  }

  addFileReference(fileReference: FileReference): void {
    this.floorplanDesignStore.getState().addFileReference(fileReference);
  }

  deleteFileReference(fileReference: FileReference): void {
    this.floorplanDesignStore.getState().removeFileReference(fileReference);
  }

  //
  // Floorplan
  //

  /**
   * @warning
   * This getter is only to be used for a non 'reactive' result.
   */
  getFloorplan(): Floorplan | undefined {
    return this.floorplanDesignStore.getState().floorplan;
  }

  setFloorplan(floorplan: Floorplan): void {
    this.replaceFullFloorplan(floorplan);
  }

  //
  // Tracks
  //

  /**
   * @warning
   * This getter is only to be used for a non 'reactive' result.
   */
  getTracks(): Track[] | undefined {
    return this.floorplanDesignStore.getState().tracks;
  }

  setTracks(tracks: Track[]): void {
    // clear current tracks
    this.canvasSvc.removeCanvasElements({
      method: 'byTag',
      tags: [FDDM_TAG.TRACK_PIECE, FDDM_TAG.SWEPT_PATH],
    });

    this.floorplanDesignStore.getState().setTracks(tracks);
    this.canvasSvc.updateElements({ tracks });
  }

  //
  // Selection
  //

  public deleteSelection(selectedIds: string[]): void {
    if (selectedIds.length === 0) return;

    const { design, getTemplatesByIds, getGroupsByIds } = this.floorplanDesignStore.getState();

    const templates = getTemplatesByIds(selectedIds);
    const selectedGroups = getGroupsByIds(selectedIds);

    const selectedGroupIds = new Set(selectedGroups.map((group) => group.id));
    const selectedGroupChildIds = new Set(selectedGroups.flatMap((group) => group.areaIds));

    const templateAreaIds = new Set(
      templatesToFlatCollection(templates).flatMap((template) => template.group.areaIds),
    );

    const selectedIdsWithoutGroups = selectedIds.filter((id) => !selectedGroupIds.has(id));

    const idsToDelete = new Set([...selectedIdsWithoutGroups]);
    selectedGroupChildIds.forEach((id) => idsToDelete.delete(id));

    const nonTemplateIdsToDelete = [...idsToDelete].filter((id) => !templateAreaIds.has(id));

    const selectedAreas = filterAreasByIds(design.areas, nonTemplateIdsToDelete);

    const selectedLogicalSpaces = design.logicalSpaces.filter((logicalSpace) =>
      nonTemplateIdsToDelete.includes(logicalSpace.id),
    );

    const selectedObstacles = design.obstacles.filter((obstacle) =>
      nonTemplateIdsToDelete.includes(obstacle.id),
    );

    const selectedFlows = nonTemplateIdsToDelete.flatMap((id) => {
      const flow = this.getFlow(id);

      return flow ? [flow] : [];
    });

    if (selectedFlows.length > 0) {
      this.deleteFlows(selectedFlows);
    }

    if (!hasNoTemplates(templates)) {
      this.deleteTemplates(templates);
    }

    if (selectedGroups.length > 0) {
      this.deleteGroups(selectedGroups);
    }

    if (!hasNoAreas(selectedAreas)) {
      this.deleteAreas(selectedAreas);
    }

    if (selectedLogicalSpaces.length > 0) {
      this.deleteLogicalSpaces(selectedLogicalSpaces);
    }

    if (selectedObstacles.length > 0) {
      this.deleteObstacles(selectedObstacles);
    }
  }

  mirrorSelectionHorizontally(selectedIds: string[]): void {
    this.transformSelection(selectedIds, 'mirror-horizontal');
  }

  mirrorSelectionVertically(selectedIds: string[]): void {
    this.transformSelection(selectedIds, 'mirror-vertical');
  }

  rotateSelection90Degrees(selectedIds: string[]): void {
    this.transformSelection(selectedIds, 'rotate-90-cw');
  }

  changeName(id: string, newName: string): boolean {
    // const template = this.floorplanDesignStore.getState().getTemplateById(id);
    const area = this.floorplanDesignStore.getState().getAreaById(id);
    const obstacle = this.floorplanDesignStore.getState().getObstacleById(id);
    const logicalSpace = this.floorplanDesignStore.getState().getLogicalSpaceById(id);
    const group = this.floorplanDesignStore.getState().getGroupById(id);
    const { design, uxonly } = this.floorplanDesignStore.getState();

    // if (template) {
    //   const uniqueName = templatesToFlatCollection(design.templates)
    //     .filter((item) => item.group.id !== template.group.id)
    //     .every((item) => item.group.name !== newName);
    //   if (!uniqueName) {
    //     return false;
    //   }

    //   this.floorplanDesignStore.getState().updateTemplate({
    //     ...template,
    //     group: {
    //       ...template.group,
    //       name: newName,
    //     },
    //   });

    //   return true;
    // }

    if (area) {
      const uniqueName = areasToFlatCollection(design.areas)
        .filter((item) => item.id !== area.id)
        .every((item) => item.name !== newName);
      if (!uniqueName) {
        return false;
      }

      this.renameArea(area, newName);
      return true;
    }

    if (obstacle) {
      const uniqueName = design.obstacles
        .filter((item) => item.id !== obstacle.id)
        .every((item) => item.name !== newName);
      if (!uniqueName) {
        return false;
      }
      this.updateObstacle({
        ...obstacle,
        name: newName,
      });
      return true;
    }

    if (logicalSpace) {
      const uniqueName = design.logicalSpaces
        .filter((item) => item.id !== logicalSpace.id)
        .every((item) => item.name !== newName);
      if (!uniqueName) {
        return false;
      }
      this.updateLogicalSpace({
        ...logicalSpace,
        name: newName,
      });
      return true;
    }

    if (group) {
      const uniqueName = uxonly.groups
        .filter((item) => item.id !== group.id)
        .every((item) => item.name !== newName);
      if (!uniqueName) {
        return false;
      }
      this.updateGroup({
        ...group,
        name: newName,
      });
      return true;
    }

    return false;
  }

  //
  // Preferences
  //

  getPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
  ): FddmPreferenceValues[PreferenceKey] {
    return this.floorplanDesignStore.getState().getPreference(key);
  }

  setPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
    value: FddmPreferenceValues[PreferenceKey],
  ): void {
    this.floorplanDesignStore.getState().setPreference(key, value);
  }

  resetPreferences(): void {
    this.floorplanDesignStore.getState().resetPreferences();
  }

  triggerAutosave(): void {
    // Autosave is handled by FddmServiceAutosaveDecorator but sometimes required to be called manually (e.g. undo/redo)
  }

  disposeState(): void {
    this.floorplanDesignStore.getState().reset();
  }

  //
  // Private methods
  //

  private getJitRelatedAreaIds(areaIds: string[]): string[] {
    const areaIdSet = new Set(areaIds);
    const { design, getHighwayBySegmentId } = this.floorplanDesignStore.getState();
    const relatedJitConfigs = design.justInTimeRoutingConfigs.filter(
      (config) =>
        config.targetAreaIds.some((id) => areaIdSet.has(id)) ||
        config.waitAreaIds.some((id) => areaIdSet.has(id)) ||
        areaIdSet.has(getHighwayBySegmentId(config.highwayAreaId)?.id ?? ''),
    );
    return relatedJitConfigs.length > 0
      ? getAreasRelatedToJitConfigs(relatedJitConfigs, getHighwayBySegmentId)
      : [];
  }

  private syncCutoutIds(
    newCollisions: Collision[],
    removedCollisions: Collision[],
    updatedAreas: Areas = createEmptyAreas(),
  ): Areas {
    const areaUpdates = createEmptyAreas();
    const areaToUpdateMap: Map<string, Area> = new Map();
    areasToFlatCollection(updatedAreas).forEach((area) => areaToUpdateMap.set(area.id, area));
    const { collisionState } = this.collisionStore.getState();
    const { getAreaByAreaIdOrSegmentId, getObstacleById, getLogicalSpaceById } =
      this.floorplanDesignStore.getState();

    const areasFromNewCollisions = getAreasFromCollisions(
      newCollisions,
      getAreaByAreaIdOrSegmentId,
    );
    const areasFromRemovedCollisions = getAreasFromCollisions(
      removedCollisions,
      getAreaByAreaIdOrSegmentId,
    );
    const areasFromPersistedCollisions = getAreasFromPersistedCollisions(
      updatedAreas,
      collisionState,
      areasFromNewCollisions,
      getAreaByAreaIdOrSegmentId,
    );

    const areasFlat = uniqueBy(
      [
        ...areasToFlatCollection(updatedAreas),
        ...areasFromNewCollisions.filter((a) => !areaToUpdateMap.has(a.id)),
        ...areasFromRemovedCollisions.filter((a) => !areaToUpdateMap.has(a.id)),
        ...areasFromPersistedCollisions.filter((a) => !areaToUpdateMap.has(a.id)),
      ],
      (area) => area.id,
    );

    areasFlat.forEach((fromArea) => {
      const currentCutOutIds = areaSupportsCutOutIds(fromArea) ? fromArea.cutOutIds : [];
      const fromAreaIsHighway = isHighwayArea(fromArea);
      const fromIds = fromAreaIsHighway ? fromArea.segments.flatMap((s) => s.id) : [fromArea.id];

      const lostOverlaps = removedCollisions.filter(
        (collision) => fromIds.includes(collision.idA) || fromIds.includes(collision.idB),
      );
      const newOverlaps = newCollisions.filter(
        (collision) => fromIds.includes(collision.idA) || fromIds.includes(collision.idB),
      );
      const persistedOverlaps = getPersistedOverlaps(
        fromIds,
        collisionState,
        removedCollisions,
        newCollisions,
      );
      const persistedOverlapActions = persistedOverlaps.flatMap((collision) => {
        const action = getPersistedOverlapAction(
          collision,
          fromArea,
          fromIds,
          updatedAreas,
          getAreaByAreaIdOrSegmentId,
        );
        return action ? [action] : [];
      });

      const persistedOverlapsToAdd = persistedOverlapActions
        .filter((item) => item.action === 'add')
        .map((item) => item.otherId);
      const persistedOverlapsToRemove = persistedOverlapActions
        .filter((item) => item.action === 'remove')
        .map((item) => item.otherId);
      const removedCutOutIds = lostOverlaps.flatMap((collision) => {
        const otherId = fromIds.includes(collision.idA) ? collision.idB : collision.idA;
        return [otherId];
      });
      const newCutOutIds = getCutoutsFromCollisions(
        fromArea,
        fromIds,
        newOverlaps,
        areaToUpdateMap,
        updatedAreas,
        getAreaByAreaIdOrSegmentId,
        getObstacleById,
        getLogicalSpaceById,
      );

      const resultingCutOutIds = unique([
        ...currentCutOutIds.filter(
          (toId) => !removedCutOutIds.includes(toId) && !persistedOverlapsToRemove.includes(toId),
        ),
        ...newCutOutIds,
        ...persistedOverlapsToAdd,
      ]);

      if (areaSupportsCutOutIds(fromArea)) {
        areaToUpdateMap.set(fromArea.id, buildAreaWithCutOutIds(fromArea, resultingCutOutIds));
      }
    });

    areaToUpdateMap.forEach((area) => pushAreaToItsCollection(areaUpdates, area));
    return areaUpdates;
  }

  private fitStationSpecToAreaWidth(area: Area): Area {
    return fitAreaStationSpecToAreaWidth(area, this.getLoadTypeDimensionsById());
  }

  private fitStationSpecsToAreaWidth(areas: Areas): Areas {
    return fitAreaStationSpecsToAreaWidth(areas, this.getLoadTypeDimensionsById());
  }

  private adjustAreaForDominantAxisChange(area: Area): Area {
    const previousArea = this.floorplanDesignStore.getState().getAreaById(area.id);

    return adjustAreaForDominantAxisChange(previousArea, area);
  }

  private adjustAreasForAxisChanges(areas: Areas): Areas {
    const adjustedAreas = createEmptyAreas();

    areasToFlatCollection(areas).forEach((area) => {
      pushAreaToItsCollection(adjustedAreas, this.adjustAreaForDominantAxisChange(area));
    });

    return adjustedAreas;
  }

  private getLoadTypeDimensionsById(): ReturnType<typeof createLoadTypeDimensionsById> {
    return createLoadTypeDimensionsById(this.floorplanDesignStore.getState().setup.loadTypes);
  }

  private invalidateValidation(): void {
    FpsServiceFactory.getInstance().createFpsService().setStep(Step.Validate);

    if (this.floorplanDesignStore.getState().tracks) this.setTracks([]);

    this.canvasSvc.removeCanvasElements({
      method: 'byTag',
      tags: [FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION],
    });
  }

  private renameArea(area: Area, newName: string): void {
    const renamedArea = areaFactory.copyArea(area, {
      id: area.id,
      name: newName,
    });

    this.invalidateValidation();
    this.floorplanDesignStore.getState().updateArea(renamedArea);

    const areas = createEmptyAreas();
    pushAreaToItsCollection(areas, renamedArea);
    this.canvasSvc.updateElements({ areas });

    // A rename updates generated station/POI ids. Regenerate from the complete model so
    // dependent POI connections are rebuilt with their existing inter-area configuration.
    this.generateFullFloorplanAndApply();
  }

  private async generateFullFloorplanAndApply(): Promise<void> {
    const response = await FloorplanServiceConnector.getInstance().generateFloorplan(
      this.floorplanDesignStore.getState().getModel(),
    );
    if (!response || !response.floorplan) return;
    this.replaceFullFloorplan(response.floorplan);
  }

  private async generatePartialFloorplanAndApply(itemIds: string[]): Promise<void> {
    if (itemIds.length === 0) return;
    const floorplanRevision = this.convertToPartialModel(itemIds);

    if (!floorplanRevision) {
      return;
    }

    const requestedAreaIds = areasToFlatCollection(floorplanRevision.design.areas).map(
      (area) => area.id,
    );

    const response = await FloorplanServiceConnector.getInstance().generateFloorplan(
      floorplanRevision,
    );

    if (!response || !response.floorplan) {
      return;
    }

    const existingAreaIds = new Set(
      areasToFlatCollection(this.floorplanDesignStore.getState().design.areas).map(
        (area) => area.id,
      ),
    );

    const requestIsStillValid = requestedAreaIds.every((areaId) => existingAreaIds.has(areaId));

    if (!requestIsStillValid) {
      return;
    }

    const floorplanElementsToRemove = this.removeFloorplanElementsByAreas(
      floorplanRevision.design.areas,
    );

    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids: floorplanElementsToRemove,
    });

    this.mergePartialFloorplan(response.floorplan);
  }

  private convertToPartialModel(itemIds: string[]): FloorplanRevision | undefined {
    const {
      getAreaById,
      getAreasByIds,
      getHighwayBySegmentId,
      getObstacleById,
      getObstaclesByIds,
      getLogicalSpaceById,
      getLogicalSpacesByIds,
      getAreaByAreaIdOrSegmentId,
      getRelatedJitRoutingConfigs,
      uxonly,
      setup,
      metaData,
    } = this.floorplanDesignStore.getState();
    const collisionState = this.collisionStore.getState().collisionState;

    // items in itemIds
    const primaryAreas = getAreasByIds(itemIds);
    const primaryObstacles = getObstaclesByIds(itemIds);
    const primaryLogicalSpaces = getLogicalSpacesByIds(itemIds);

    // --Overlap--
    // items that overlap with the primary items
    const overlappingAreas: Set<Area> = new Set();
    const overlappingObstacles: Set<Obstacle> = new Set();
    const overlappingLogicalSpaces: Set<LogicalSpace> = new Set();

    // get areas that the primary obstacles and logical spaces are colliding with
    [...primaryLogicalSpaces, ...primaryObstacles].forEach((item) => {
      const intersectingObjects = collisionState.get(item.id) || [];
      const intersectingAreas = uniqueBy(
        intersectingObjects
          .map((id) => getAreaByAreaIdOrSegmentId(id))
          .filter((area): area is Area => area !== undefined),
        (item) => item.id,
      );
      intersectingAreas.forEach(overlappingAreas.add, overlappingAreas);
    });

    // get logical spaces and obstacles that the primary areas are colliding with
    areasToFlatCollection(primaryAreas).forEach((area) => {
      const fromIds = isHighwayArea(area) ? area.segments.flatMap((s) => s.id) : [area.id];

      const intersectingObjects = fromIds.flatMap((id) => collisionState.get(id) || []);
      const intersectingObstacles = uniqueBy(
        getObstaclesByIds(intersectingObjects),
        (item) => item.id,
      );
      intersectingObstacles.forEach(overlappingObstacles.add, overlappingObstacles);
      const intersectingLogicalSpaces = uniqueBy(
        getLogicalSpacesByIds(intersectingObjects),
        (item) => item.id,
      );
      intersectingLogicalSpaces.forEach(overlappingLogicalSpaces.add, overlappingLogicalSpaces);
    });

    // --Cutouts--
    const primaryAreasFlat = areasToFlatCollection(primaryAreas);
    const resolvedReferences: Set<string> = new Set(primaryAreasFlat.map((item) => item.id));
    const supplementaryAreaIdReferencedByCutOuts: string[] = [];

    // Get all areas that are referenced by cutouts of the primary areas, and recursively get all areas that are referenced by cutouts of those areas, etc.
    [...primaryAreasFlat, ...overlappingAreas].forEach((area) => {
      getAreaItsDeeplyReferencedCutOutAreaIds(
        area.id,
        resolvedReferences,
        supplementaryAreaIdReferencedByCutOuts,
        getAreaById,
        getHighwayBySegmentId,
      );
    });

    const supplementaryAreasDeeplyReferencedByCutOuts = areasToFlatCollection(
      getAreasByIds(supplementaryAreaIdReferencedByCutOuts),
    );

    const areas = createEmptyAreas();
    const areasFlat = uniqueBy(
      [...primaryAreasFlat, ...supplementaryAreasDeeplyReferencedByCutOuts, ...overlappingAreas],
      (item) => item.id,
    );
    areasFlat.forEach((area) => pushAreaToItsCollection(areas, area));

    // get all obstacles referenced by cutouts
    const supplementaryObstaclesReferencedByCutOuts = areasFlat.flatMap((area) => {
      return getObstaclesReferencedByCutOutIds(area, getObstacleById);
    });

    // get all logical spaces referenced by cutouts
    const supplementaryLogicalSpacesReferencedByCutOuts = areasFlat.flatMap((area) => {
      return getLogicalSpacesReferencedByCutOutIds(area, getLogicalSpaceById);
    });

    return {
      metaData: metaData,
      uxonly: uxonly,
      setup: setup,
      design: {
        areas,
        interAreaConnections: [],
        obstacles: uniqueBy(
          [
            ...primaryObstacles,
            ...Array.from(overlappingObstacles),
            ...supplementaryObstaclesReferencedByCutOuts,
          ],
          (item) => item.id,
        ),
        templates: {
          lassies: [],
          lassieMinis: [],
          shuttleRacks: [],
        },
        flows: { flowStops: [], flows: [], dependentFlows: [] },
        justInTimeRoutingConfigs: getRelatedJitRoutingConfigs([
          ...areas.highwayAreas.map((area) => area.id),
          ...areas.stationaryAreas.map((area) => area.id),
          ...areas.singularStationAreas.map((area) => area.id),
        ]).flat(),
        logicalSpaces: uniqueBy(
          [
            ...primaryLogicalSpaces,
            ...Array.from(overlappingLogicalSpaces),
            ...supplementaryLogicalSpacesReferencedByCutOuts,
          ],
          (item) => item.id,
        ),
      },
      floorplan: {
        stations: [],
        vehicleLayouts: [],
      },
      tracks: [],
    };
  }

  private replaceFullFloorplan(floorplan: Floorplan): void {
    const {
      applyFloorplan: mergeFloorplan,
      setStations,
      setVehicleLayouts,
    } = this.floorplanDesignStore.getState();

    /* Single batched store mutation. Returns the ids that need to be
   removed from the canvas (stations, pois, pose markers, gate mappings). */
    const { removedElementIds } = mergeFloorplan(floorplan);

    setStations(floorplan.stations);
    setVehicleLayouts(floorplan.vehicleLayouts);

    this.syncFloorplanCanvas(removedElementIds);
  }

  private mergePartialFloorplan(floorplan: Floorplan): void {
    const { applyFloorplan: mergeFloorplan } = this.floorplanDesignStore.getState();

    /* The affected areas were removed before applying this response.
   Merge the partial response into the remaining floorplan. */
    const { removedElementIds } = mergeFloorplan(floorplan);

    this.syncFloorplanCanvas(removedElementIds);
  }

  private syncFloorplanCanvas(removedElementIds: string[]): void {
    if (removedElementIds.length > 0) {
      this.canvasSvc.removeCanvasElements({
        method: 'byId',
        ids: removedElementIds,
      });
    }

    this.canvasSvc.removeCanvasElements({
      method: 'byTag',
      tags: [FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION],
    });

    const { design, floorplan } = this.floorplanDesignStore.getState();

    this.canvasSvc.updateElements({
      flowLayer: design.flows,
      stations: floorplan.stations,
      vehicleLayouts: floorplan.vehicleLayouts,
    });
  }

  private updateInterAreaConnectionsBasedOnTemplates(templates: Templates): void {
    const templateCollection = templatesToFlatCollection(templates);

    const areas: Areas = createEmptyAreas();
    templateCollection.forEach((template) => {
      const areasInTemplate = this.floorplanDesignStore
        .getState()
        .getAreasInTemplate(template.group.id);
      if (!areasInTemplate) return;
      areasInTemplate.forEach((area) => pushAreaToItsCollection(areas, area));
    });

    this.updateInterAreaConnectionsBasedOnAreas(areas);
  }

  private updateInterAreaConnectionsBasedOnAreas(areaUpdates: Areas): void {
    const { getRelatedTo } = this.floorplanDesignStore.getState().interAreaConnectionActions;
    const relatedConnections = getRelatedTo([
      ...areasToFlatCollection(areaUpdates).flatMap((area) =>
        isHighwayArea(area) ? area.segments.map((segment) => segment.id) : area.id,
      ),
    ]);

    const { connectionsToAdd, connectionsToSwitch, connectionsToRemove } =
      InterAreaConnectionUtils.determineConnectionUpdates(
        areaUpdates,
        this.floorplanDesignStore.getState().design,
        this.collisionStore.getState().collisionState,
      );

    if (connectionsToRemove.length > 0) {
      this.deleteInterAreaConnections(connectionsToRemove);
    }

    if (connectionsToAdd.length > 0) {
      this.addInterAreaConnections(connectionsToAdd);
    }

    // switched connections
    // these connections switch type, and therefore change element type on canvas
    // we need to remove and re-add them to update the canvas elements accordingly
    // NOTE: we bypass the public updateInterAreaConnections here intentionally —
    // auto-switches must NOT be marked as createdExplicitly.
    const switchIds = connectionsToSwitch.map((item) => item.id);
    this.canvasSvc.removeCanvasElements({ method: 'byId', ids: switchIds });
    connectionsToSwitch.forEach((connection) =>
      this.floorplanDesignStore.getState().interAreaConnectionActions.update(connection),
    );
    if (connectionsToSwitch.length > 0) {
      this.canvasSvc.updateElements({ interAreaConnections: connectionsToSwitch });
      this.invalidateValidation();
    }

    // existing connections related to the updated areas, that are not switched or deleted
    // their elements need to be updated to reflect position changes
    const deleteIds = connectionsToRemove.map((item) => item.id);
    const connectionsToKeep = relatedConnections.filter(
      (item) => !switchIds.includes(item.id) && !deleteIds.includes(item.id),
    );

    this.canvasSvc.updateElements({
      interAreaConnections: connectionsToKeep,
    });
  }

  /** @description Removes floorplan elements linked to the given area ids from the store and returns their ids for canvas cleanup. */
  private removeFloorplanElementsByAreas(areas: Areas): string[] {
    const {
      floorplan,
      removeStations,
      removePois,
      removePoiGroups,
      removeGateMappings,
      removePoiConnections,
      deletePoiGroups,
    } = this.floorplanDesignStore.getState();

    // use segment ids for highway areas
    const areaCollection = areasToFlatCollection(areas);
    const areaAndSegmentBaseIdSet = new Set(
      [
        ...areaCollection.map((area) =>
          isHighwayArea(area)
            ? IdUtils.destructureIdWithSegmentIndex(area.segments[0].id).areaId
            : area.id,
        ),
      ].flat(),
    );

    const stationIdsToRemove = floorplan.stations
      .filter((station) =>
        station.areaIds.some((areaId) => {
          const normalizedAreaId = isUuid(areaId)
            ? areaId
            : IdUtils.destructureIdWithSegmentIndex(areaId).areaId;

          return areaAndSegmentBaseIdSet.has(normalizedAreaId);
        }),
      )
      .map(({ id }) => id);

    if (stationIdsToRemove.length > 0) {
      removeStations(stationIdsToRemove);
    }

    const poiIdsByVehicleType = floorplan.vehicleLayouts.reduce<
      Array<{ vehicleTypeId: string; poiIds: string[] }>
    >((result, vehicleLayout) => {
      const poiIds = vehicleLayout.pois
        .filter((poi) =>
          areaAndSegmentBaseIdSet.has(
            isUuid(poi.areaId)
              ? poi.areaId
              : IdUtils.destructureIdWithSegmentIndex(poi.areaId).areaId,
          ),
        )
        .map(({ id }) => id);

      if (poiIds.length === 0) {
        return result;
      }

      result.push({
        vehicleTypeId: vehicleLayout.vehicleTypeId,
        poiIds,
      });

      return result;
    }, []);

    const poiIdsToRemove = poiIdsByVehicleType.flatMap(({ poiIds }) => poiIds);
    if (poiIdsToRemove.length > 0) {
      const poiIdsToRemoveSet = new Set(poiIdsToRemove);
      const poiConnectionIdsToRemove = floorplan.vehicleLayouts.flatMap((vehicleLayout) =>
        vehicleLayout.poiConnections
          .filter(
            (conn) =>
              poiIdsToRemoveSet.has(conn.startPoiId) || poiIdsToRemoveSet.has(conn.endPoiId),
          )
          .map((conn) => conn.id),
      );

      poiIdsByVehicleType.forEach(({ vehicleTypeId, poiIds }) => {
        removePois(vehicleTypeId, poiIds);
        removePoiGroups(vehicleTypeId, poiIds);
        removeGateMappings(vehicleTypeId, poiIds);
        removePoiConnections(vehicleTypeId, poiIds);
        deletePoiGroups(vehicleTypeId, poiIds);
      });

      return [
        ...stationIdsToRemove,
        ...poiIdsToRemove,
        ...getPoseIdsFromPois(poiIdsToRemove),
        ...poiConnectionIdsToRemove,
      ];
    }

    return stationIdsToRemove;
  }

  private transformSelection(selectedIds: string[], mode: TransformSelectionMode): void {
    if (selectedIds.length === 0) {
      return;
    }

    const state = this.floorplanDesignStore.getState();
    const selectedGroups = state.getGroupsByIds(selectedIds);
    const selectedGroupItemIds = selectedGroups.flatMap((group) => group.areaIds);
    const idsToTransform = [...new Set([...selectedIds, ...selectedGroupItemIds])];

    const selectedAreas = state.getAreasByIds(idsToTransform);
    const selectedObstacles = state.getObstaclesByIds(idsToTransform);
    const selectedLogicalSpaces = state.getLogicalSpacesByIds(idsToTransform);
    const selectedAreaList = areasToFlatCollection(selectedAreas);

    const hasTransformableSelection =
      selectedAreaList.length > 0 ||
      selectedObstacles.length > 0 ||
      selectedLogicalSpaces.length > 0;

    if (!hasTransformableSelection) {
      return;
    }

    const bounds = computeItemsBoundingBoxExtent(
      selectedAreas,
      selectedObstacles,
      selectedLogicalSpaces,
    );

    const boundsAreValid =
      Number.isFinite(bounds.x) &&
      Number.isFinite(bounds.y) &&
      Number.isFinite(bounds.width) &&
      Number.isFinite(bounds.height);

    if (!boundsAreValid) {
      return;
    }

    const pivotX = bounds.x + bounds.width / 2;
    const pivotY = bounds.y + bounds.height / 2;

    const areaUpdates = createEmptyAreas();

    selectedAreaList.forEach((area) => {
      pushAreaToItsCollection(areaUpdates, transformAreaByMode(area, pivotX, pivotY, mode));
    });

    if (!hasNoAreas(areaUpdates)) {
      this.updateAreas(areaUpdates);
    }

    selectedObstacles.forEach((obstacle) => {
      this.updateObstacle(transformObstacleByMode(obstacle, pivotX, pivotY, mode));
    });

    selectedLogicalSpaces.forEach((logicalSpace) => {
      this.updateLogicalSpace(transformLogicalSpaceByMode(logicalSpace, pivotX, pivotY, mode));
    });
  }

  private getFloorplanElementIdsByVehicleTypeIds(vehicleTypeIds: string[]): string[] {
    const vehicleTypeIdSet = new Set(vehicleTypeIds);
    const { floorplan } = this.floorplanDesignStore.getState();

    const vehicleLayoutsToRemove = floorplan.vehicleLayouts.filter((vehicleLayout) =>
      vehicleTypeIdSet.has(vehicleLayout.vehicleTypeId),
    );

    const poiIdsToRemove = vehicleLayoutsToRemove.flatMap((vehicleLayout) =>
      vehicleLayout.pois.map((poi) => poi.id),
    );

    return unique([...poiIdsToRemove, ...getPoseIdsFromPois(poiIdsToRemove)]);
  }

  private removeVehiclesFromHighways(vehicleTypeIds: string[]): void {
    if (vehicleTypeIds.length === 0) {
      return;
    }

    const vehicleTypeIdsSet = new Set(vehicleTypeIds);
    const {
      design: { areas },
    } = this.floorplanDesignStore.getState();

    const updatedHighways = areas.highwayAreas.flatMap((area) => {
      const currentSupportedVehicleIds = getSupportedVehicleIds(area);
      const nextSupportedVehicleIds = currentSupportedVehicleIds.filter(
        (vehicleId) => !vehicleTypeIdsSet.has(vehicleId),
      );

      if (currentSupportedVehicleIds.length === nextSupportedVehicleIds.length) {
        return [];
      }

      return [setSupportedVehicles(area, nextSupportedVehicleIds)];
    });

    if (updatedHighways.length === 0) {
      return;
    }

    const updatedAreas: Areas = {
      highwayAreas: updatedHighways,
      rackingAreas: [],
      singularStationAreas: [],
      stationaryAreas: [],
      manualAreas: [],
    };

    this.invalidateValidation();
    this.floorplanDesignStore.getState().updateAreas(updatedAreas);
    this.canvasSvc.updateElements({ areas: updatedAreas });
  }

  private propagatedFlows(flow: Flow): Flow[] {
    const updatedFlows: Flow[] = [];
    const forwardFlows = this.getFlowsForwards(flow);
    const backwardFlows = this.getFlowsBackwards(flow);

    if (forwardFlows.length > 0 && forwardFlows[0].type === 'EXTERNAL') {
      updatedFlows.push(
        ...forwardFlows.map((f) => ({
          ...f,
          hourlyOrderCounts: flow.hourlyOrderCounts,
        })),
      );
    }
    if (backwardFlows.length > 0 && backwardFlows[0].type === 'EXTERNAL') {
      updatedFlows.push(
        ...backwardFlows.map((f) => ({
          ...f,
          hourlyOrderCounts: flow.hourlyOrderCounts,
        })),
      );
    }

    return updatedFlows;
  }

  private getFlowsForwards(flow: Flow): Flow[] {
    const { getRelatedFlows } = this.floorplanDesignStore.getState();
    const visitedFlowIds = new Set<string>([flow.id]);
    const visitedSourceIds = new Set<string>();
    const sourceIdsToVisit: string[] = [flow.destinationAreaOrFlowStopId];
    const outbound: Flow[] = [];

    while (sourceIdsToVisit.length > 0) {
      const currentSourceId = sourceIdsToVisit.shift();
      if (!currentSourceId || visitedSourceIds.has(currentSourceId)) {
        continue;
      }

      visitedSourceIds.add(currentSourceId);

      const nextFlows = getRelatedFlows([currentSourceId]).filter(
        (candidateFlow) =>
          candidateFlow.sourceAreaOrFlowStopId === currentSourceId &&
          !visitedFlowIds.has(candidateFlow.id),
      );

      nextFlows.forEach((nextFlow) => {
        visitedFlowIds.add(nextFlow.id);
        outbound.push(nextFlow);
        sourceIdsToVisit.push(nextFlow.destinationAreaOrFlowStopId);
      });
    }

    return outbound;
  }

  private getFlowsBackwards(flow: Flow): Flow[] {
    const { getRelatedFlows } = this.floorplanDesignStore.getState();
    const visitedFlowIds = new Set<string>([flow.id]);
    const visitedDestinationIds = new Set<string>();
    const destinationIdsToVisit: string[] = [flow.sourceAreaOrFlowStopId];
    const inbound: Flow[] = [];

    while (destinationIdsToVisit.length > 0) {
      const currentDestinationId = destinationIdsToVisit.shift();
      if (!currentDestinationId || visitedDestinationIds.has(currentDestinationId)) {
        continue;
      }

      visitedDestinationIds.add(currentDestinationId);

      const previousFlows = getRelatedFlows([currentDestinationId]).filter(
        (candidateFlow) =>
          candidateFlow.destinationAreaOrFlowStopId === currentDestinationId &&
          !visitedFlowIds.has(candidateFlow.id),
      );

      previousFlows.forEach((previousFlow) => {
        visitedFlowIds.add(previousFlow.id);
        inbound.push(previousFlow);
        destinationIdsToVisit.push(previousFlow.sourceAreaOrFlowStopId);
      });
    }

    return inbound;
  }
}
